import {HttpErrorResponse} from '@angular/common/http';
import {
  ApiActions,
  ErrorPayload,
} from '@binotel-frontend/bnt-common-components';
import {TranslateHelper} from '@binotel-frontend/bnt-common-components';
import {IApi} from '@entities/api';
import {ESnackbarType} from '@entities/ui/snackbar';
import {environment} from '@envs/environment';
import {Action} from '@ngrx/store';
import {ErrorsHelper, SnackbarHelper} from '@shared/helper';
import {OdApiErrorResponse} from '@shared/services';
import {catchError, map, of, OperatorFunction} from 'rxjs';

export enum EApiNotification {
  OnError = 'on error',
  OnErrorExceptNotFound = 'on error except not found',
  OnSuccess = 'on success',
  Always = 'always',
  Never = 'never',
}

interface IApiNotifyConfig {
  error?: string;
  success?: string;
}

type ActionWithPayload<T> = {payload: T} & Action<string>;
type FailedAction = ErrorPayload & Action<string>;

/**
 * Applies a given project function to each value emitted by the source Observable.
 * Emits a FAILED action if an error occurs. Emits a SUCCEEDED action with/without payload if a data load is successful.
 * @param action - data loading action types.
 * @param notify - Events to show notification
 * @param notifyConfig - Notification texts config
 */
export function mapOdApiActions<ResponsePayload, RequestParams>(
  action: ApiActions<
    {payload: ResponsePayload; pagination?: unknown},
    RequestParams
  >,
  notify: EApiNotification = EApiNotification.OnError,
  notifyConfig?: IApiNotifyConfig
): OperatorFunction<
  IApi<ResponsePayload>,
  ActionWithPayload<ResponsePayload> | FailedAction
> {
  return (source) =>
    source.pipe(
      map((data: IApi<ResponsePayload>) => {
        if (
          notify === EApiNotification.Always ||
          notify === EApiNotification.OnSuccess
        ) {
          SnackbarHelper.open(
            notifyConfig?.success ??
              TranslateHelper.translate('common.operation-successful')
          );
        }
        if (data?.meta?.pagination) {
          return action.succeeded({
            payload: data.data,
            pagination: data.meta.pagination,
          }) as ActionWithPayload<ResponsePayload>;
        }
        return action.succeeded({
          payload: data?.data ?? null,
        }) as ActionWithPayload<ResponsePayload>;
      }),
      catchError((e: OdApiErrorResponse) => {
        const notifyOnError =
          notify === EApiNotification.Always ||
          notify === EApiNotification.OnError ||
          notify === EApiNotification.OnErrorExceptNotFound;
        const suppressNotFound =
          notify === EApiNotification.OnErrorExceptNotFound &&
          ErrorsHelper.isNotFoundResponse(e);
        if (notifyOnError && e.status !== 0 && !suppressNotFound) {
          const apiError = ErrorsHelper.getApiErrorAsString(e.error, false);
          SnackbarHelper.open(
            notifyConfig?.error ||
              apiError ||
              TranslateHelper.translate('common.error-occurred'),
            ESnackbarType.Error
          );
        }
        if (
          !(
            e instanceof OdApiErrorResponse ||
            (e as unknown) instanceof HttpErrorResponse
          )
        ) {
          ErrorsHelper.error(e);
          if (!environment.production) {
            console.error(e);
          }
        }

        return of(
          action.failed({
            error: ErrorsHelper.getErrorLabelsFromIApi(e),
            httpStatus: e.status,
          }) as FailedAction
        );
      })
    );
}
